import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Grid, Row, Col } from 'react-flexbox-grid'
import { Divider, Table, message, Tooltip, Input, Space, Button } from 'antd'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { getUser } from '../../../reducers'
import { db, firestore, storage } from '../../../config/Fire'
import * as Constants from '../../../constants'
import './styles.css'

class MedicalLicenses extends Component {
    state = {
        records: [],
        users: {},
        loading: true,
        tableData: [],
        searchText: '',
        searchedColumn: ''
    }

    componentDidMount() {
        this.loadData()
    }

    loadData = async () => {
        this.setState({ loading: true })

        try {
            // Cargar usuarios
            const usersSnap = await db.ref('users').once('value')
            const usersLoaded = {}
            if (usersSnap.exists()) {
                usersSnap.forEach(userSnap => {
                    const user = userSnap.val()
                    if (user.id && user.userType === 'CONSTRUCTION') {
                        usersLoaded[user.id] = user
                    }
                })
            }
            this.setState({ users: usersLoaded })

            // Cargar licencias médicas
            const querySnapshot = await firestore.collection('medical_license').get()
            const recordsLoaded = []

            if (!querySnapshot.empty) {
                querySnapshot.forEach(doc => {
                    recordsLoaded.push({
                        id: doc.id,
                        ...doc.data()
                    })
                })
            }

            this.setState({ records: recordsLoaded }, () => {
                this.processData()
            })
        } catch (err) {
            console.log('Error getting data: ' + JSON.stringify(err))
            message.error('Error al cargar la información')
            this.setState({ loading: false })
        }
    }

    processData = () => {
        const { records, users } = this.state
        const usersArray = Object.values(users)

        const tableData = records.map(record => {
            let user = {}

            if (record.dni) {
                const userByDni = usersArray.find(u => u.dni && String(u.dni).trim() === String(record.dni).trim())
                if (userByDni) {
                    user = userByDni
                }
            }

            if (!user.id && record.userId && users[record.userId]) {
                user = users[record.userId]
            }

            return {
                key: record.id,
                ...record,
                name: user.name || '--',
                lastName: user.lastName || '',
                email: user.email || '--',
                category: user.category || '--',
                dni: record.dni || user.dni || '--'
            }
        })

        this.setState({ tableData, loading: false })
    }

    formatDate = (dateVal) => {
        if (!dateVal) return '--'
        const dateObj = new Date(dateVal)
        if (isNaN(dateObj)) return '--'
        return `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`
    }

    handleDownload = (fileUrl, name, lastName) => {
        if (!fileUrl) {
            message.warning('No hay archivo disponible')
            return
        }

        const cleanName = (name || '').replace(/\s+/g, '_')
        const cleanLastName = (lastName || '').replace(/\s+/g, '_')
        
        let extension = 'pdf'
        try {
            const pathUrl = fileUrl.split('?')[0]
            const splitted = pathUrl.split('.')
            if (splitted.length > 1) {
                const ext = splitted.pop().toLowerCase()
                if (['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
                    extension = ext
                }
            }
        } catch (e) {
            console.log('Error getting file extension', e)
        }
        
        const fileNameBase = `licencia_medica_${cleanName}${cleanLastName ? `_${cleanLastName}` : ''}`
        const fileName = `${fileNameBase}.${extension}`.toLowerCase()

        let urlPromise
        if (fileUrl.startsWith('http')) {
            urlPromise = Promise.resolve(fileUrl)
        } else {
            urlPromise = storage.ref().child(fileUrl).getDownloadURL()
        }

        urlPromise
            .then(url => {
                message.info('Descargando archivo...')
                return fetch(url)
            })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok')
                return response.blob()
            })
            .then(blob => {
                const urlBlob = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = urlBlob
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                a.remove()
                window.URL.revokeObjectURL(urlBlob)
                message.success('Archivo descargado exitosamente')
            })
            .catch(err => {
                console.log('Error downloading file: ' + err)
                message.error('Error al descargar el archivo. Posible problema de permisos (CORS) o archivo no encontrado.')
            })
    }

    getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => {
                        this.searchInput = node
                    }}
                    placeholder={`Buscar ${dataIndex === 'name' ? 'Nombre o Apellido' : dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => this.handleSearchColumn(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => this.handleSearchColumn(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Buscar
                    </Button>
                    <Button onClick={() => this.handleResetColumn(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => {
            if (dataIndex === 'name') {
                const fullName = `${record.name || ''} ${record.lastName || ''}`.trim().toLowerCase()
                return fullName.includes(value.toLowerCase())
            }
            return record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : ''
        },
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => this.searchInput.select(), 100)
            }
        },
    })

    handleSearchColumn = (selectedKeys, confirm, dataIndex) => {
        confirm()
        this.setState({
            searchText: selectedKeys[0],
            searchedColumn: dataIndex,
        })
    }

    handleResetColumn = clearFilters => {
        clearFilters()
        this.setState({ searchText: '' })
    }

    render() {
        const { tableData, loading } = this.state

        const columns = [
            {
                title: 'Nombre y Apellido',
                dataIndex: 'name',
                key: 'name',
                align: 'center',
                ...this.getColumnSearchProps('name'),
                render: (text, record) => {
                    const fullName = `${record.name || ''} ${record.lastName || ''}`.trim()
                    return fullName || '--'
                }
            },
            {
                title: 'Correo',
                dataIndex: 'email',
                key: 'email',
                align: 'center'
            },
            {
                title: 'Categoría',
                dataIndex: 'category',
                key: 'category',
                align: 'center',
                render: (text) => text && Constants.OPERATOR_CATEGORIES[text] ? Constants.OPERATOR_CATEGORIES[text] : text || '--'
            },
            {
                title: 'DNI',
                dataIndex: 'dni',
                key: 'dni',
                align: 'center'
            },
            {
                title: 'Fecha Inicio',
                dataIndex: 'startDate',
                key: 'startDate',
                align: 'center',
            },
            {
                title: 'Fecha Fin',
                dataIndex: 'endDate',
                key: 'endDate',
                align: 'center',
            },
            {
                title: 'Archivo',
                key: 'fileUrl',
                align: 'center',
                render: (_, record) => {
                    if (!record.fileUrl) return '--'
                    return (
                        <DownloadOutlined
                            style={{ fontSize: '20px', cursor: 'pointer', color: '#1890ff' }}
                            onClick={() => this.handleDownload(record.fileUrl, record.name, record.lastName)}
                        />
                    )
                }
            },
            {
                title: 'Observaciones',
                dataIndex: 'observation',
                key: 'observation',
                align: 'center',
                render: (text) => {
                    const obs = text || '--'
                    return (
                        <Tooltip title={obs}>
                            <span style={{
                                display: 'inline-block',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {obs}
                            </span>
                        </Tooltip>
                    )
                }
            }
        ]

        return (
            <div>
                <Grid>
                    <Divider>Licencias Médicas</Divider>
                    <Row>
                        <Col xs={12}>
                            <Table
                                columns={columns}
                                dataSource={tableData}
                                loading={loading}
                                rowKey="id"
                                pagination={{ pageSize: 10 }}
                            />
                        </Col>
                    </Row>
                </Grid>
            </div>
        )
    }
}

MedicalLicenses.propTypes = {}

const mapStateToProps = state => ({
    user: getUser(state)
})

export default connect(mapStateToProps)(MedicalLicenses)
